import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, AlertTriangle, XCircle, FileCheck2 } from 'lucide-react';
import Header from '../components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const outcomes = [
  {
    id: 'clear',
    title: 'Clear',
    desc: 'Pass the background check and unlock bookings instantly.',
    icon: ShieldCheck,
    tone: 'bg-emerald-500',
    btn: 'Pass Check (Clear)',
  },
  {
    id: 'consider',
    title: 'Consider',
    desc: 'Simulate a flagged record requiring admin review.',
    icon: AlertTriangle,
    tone: 'bg-amber-500',
    btn: 'Flag for Review',
  },
  {
    id: 'suspended',
    title: 'Suspended',
    desc: 'Simulate incomplete/unverifiable info — cleaner must retry.',
    icon: XCircle,
    tone: 'bg-rose-500',
    btn: 'Suspend Report',
  },
];

const CheckrMock = ({ user, setUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const candidateId = searchParams.get('candidate_id');
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (result) => {
    if (!candidateId) {
      setError('Missing candidate_id');
      return;
    }
    setSubmitting(result);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/background-check/mock-resolve`,
        { candidate_id: candidateId, result },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/dashboard/cleaner');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to resolve mock check');
      setSubmitting(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} setUser={setUser} />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 mb-8 flex items-start gap-3" data-testid="checkr-mock-banner">
          <FileCheck2 className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-heading font-bold text-amber-900 mb-1">DEMO MODE — MOCKED Checkr Flow</h3>
            <p className="text-sm text-amber-800">
              This is a stand-in for Checkr's hosted consent form. In production, cleaners would securely enter
              SSN/DOB on Checkr's PCI-compliant page. Pick an outcome below to simulate what Checkr returns.
            </p>
          </div>
        </div>

        <h1 className="font-heading text-3xl font-bold text-text-primary mb-2">Choose a simulated outcome</h1>
        <p className="text-text-secondary mb-8">
          Candidate ID: <code className="text-xs bg-surface-muted px-2 py-1 rounded">{candidateId || 'missing'}</code>
        </p>

        {error && (
          <div className="bg-rose-50 border border-rose-300 rounded-xl p-4 mb-6 text-sm text-rose-700" data-testid="checkr-mock-error">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {outcomes.map((o) => {
            const Icon = o.icon;
            return (
              <div key={o.id} className="bg-surface rounded-2xl p-6 border border-border flex flex-col">
                <div className={`w-12 h-12 rounded-full ${o.tone} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-lg font-bold text-text-primary mb-2">{o.title}</h3>
                <p className="text-sm text-text-secondary mb-6 flex-1">{o.desc}</p>
                <button
                  onClick={() => submit(o.id)}
                  disabled={!!submitting}
                  className={`${o.tone} hover:opacity-90 disabled:opacity-50 text-white font-semibold py-3 rounded-full transition-opacity`}
                  data-testid={`checkr-mock-${o.id}-button`}
                >
                  {submitting === o.id ? 'Submitting...' : o.btn}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CheckrMock;
